/**
 * Screen-aware starter prompts for an empty Ask Chckvet thread.
 *
 * The agent already receives the route as UI context; these just give staff a
 * first move that matches the page they're on, instead of a blank "how can I
 * help?" box.
 */

export type AskChckvetStarter = {
    /** Short chip label. */
    label: string
    /** Full prompt inserted into the composer. */
    prompt: string
}

type StarterRule = {
    /** Matched against the pathname with `startsWith`. */
    prefix: string
    starters: AskChckvetStarter[]
}

const GENERAL_STARTERS: AskChckvetStarter[] = [
    {
        label: 'Why no times?',
        prompt: 'Clients say there are no appointment times available online. Can you check why?',
    },
    {
        label: 'Reminder not sent',
        prompt: "A client didn't get their appointment reminder. Can you work out why?",
    },
    {
        label: 'Where is a setting?',
        prompt: 'Where in the dashboard do I change our online booking hours?',
    },
]

/**
 * Longest matching prefix wins, so `/booking/scheduling-rules` beats
 * `/booking`.
 */
const STARTER_RULES: StarterRule[] = [
    {
        prefix: '/booking/availability-checker',
        starters: [
            {
                label: 'Explain these results',
                prompt: 'Explain why the availability checker is returning these results for this week.',
            },
            {
                label: 'Open a closed day',
                prompt: 'We want to start taking online bookings on Saturday. What needs to change?',
            },
        ],
    },
    {
        prefix: '/booking/scheduling-rules',
        starters: [
            {
                label: 'What does this rule do?',
                prompt: 'Explain in plain language what this scheduling rule allows and blocks.',
            },
            {
                label: 'Block new clients',
                prompt: 'We only want returning clients to book this service online. How do I set that up?',
            },
        ],
    },
    {
        prefix: '/booking/services',
        starters: [
            {
                label: 'Why not bookable?',
                prompt: "This service isn't showing up for online booking. Can you check why?",
            },
            {
                label: 'Change duration',
                prompt: 'How do I change how long this service blocks on the schedule?',
            },
        ],
    },
    {
        prefix: '/booking',
        starters: [
            {
                label: 'Why no times?',
                prompt: 'Clients say there are no appointment times available online. Can you check why?',
            },
            {
                label: 'Review booking gates',
                prompt: 'Which settings are currently limiting who can book online with us?',
            },
        ],
    },
    {
        prefix: '/engagement/notifications',
        starters: [
            {
                label: 'Why not sent?',
                prompt: "A client didn't receive this notification. Can you check the template and audience?",
            },
            {
                label: 'Still in preview?',
                prompt: 'Is this notification still in preview mode, and what does that mean for clients?',
            },
        ],
    },
    {
        prefix: '/engagement/audiences',
        starters: [
            {
                label: 'Who is included?',
                prompt: 'Explain which clients this audience includes and excludes right now.',
            },
            {
                label: 'Why is a client missing?',
                prompt: "A specific client isn't in this audience but I expected them to be. Can you check why?",
            },
        ],
    },
    {
        prefix: '/engagement/forms',
        starters: [
            {
                label: 'Form not showing',
                prompt: "This form isn't appearing during booking. Can you check the setup?",
            },
            {
                label: 'Writeback rules',
                prompt: 'What happens to the answers on this form after a client submits it?',
            },
        ],
    },
    {
        prefix: '/engagement',
        starters: [
            {
                label: 'Reminder not sent',
                prompt: "A client didn't get their appointment reminder. Can you work out why?",
            },
            {
                label: 'Audit our messaging',
                prompt: 'Which of our notifications are inactive or still in preview mode?',
            },
        ],
    },
    {
        prefix: '/analytics',
        starters: [
            {
                label: 'Explain this metric',
                prompt: 'Explain what this metric measures and how it is calculated.',
            },
            {
                label: 'Add a metric',
                prompt: 'I want to track a new metric on this report. How do I add one?',
            },
        ],
    },
    {
        prefix: '/clinic/clients',
        starters: [
            {
                label: 'Messaging history',
                prompt: 'What messages have we sent this client recently, and did they deliver?',
            },
            {
                label: 'Why no reminders?',
                prompt: "This client isn't receiving reminders. Can you check their contact settings and audiences?",
            },
        ],
    },
    {
        prefix: '/settings',
        starters: [
            {
                label: 'Explain this setting',
                prompt: 'Explain what the settings on this page control and who they affect.',
            },
            {
                label: 'Booking window',
                prompt: 'How far ahead can clients currently book with us, and where do I change it?',
            },
        ],
    },
]

/**
 * Up to three starters for the given route: page-specific first, topped up
 * with general ones so the empty state is never sparse.
 */
export function getAskChckvetStarters(pathname: string): AskChckvetStarter[] {
    const matched = STARTER_RULES.filter(rule =>
        pathname.startsWith(rule.prefix),
    ).sort((a, b) => b.prefix.length - a.prefix.length)[0]

    const pageStarters = matched?.starters ?? []
    const seen = new Set(pageStarters.map(starter => starter.prompt))
    const filler = GENERAL_STARTERS.filter(starter => !seen.has(starter.prompt))

    return [...pageStarters, ...filler].slice(0, 3)
}
